
import React from 'react';
import { SaasInstance } from '@/types/userRole';
import { TableCell, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { ExternalLink, CreditCard, MoreHorizontal, Settings, Plus } from 'lucide-react';
import InstancePlanBadge from './InstancePlanBadge';
import InstanceStatusIndicator from './InstanceStatusIndicator';

interface InstanceTableRowProps {
  instance: SaasInstance;
  onManage: (instanceId: string) => void;
  onVisit: (domain: string) => void;
  onToggleSuspend: (instanceId: string, currentStatus: string) => void;
  onManagePayments: (instanceId: string) => void;
}

const InstanceTableRow: React.FC<InstanceTableRowProps> = ({
  instance,
  onManage,
  onVisit,
  onToggleSuspend,
  onManagePayments
}) => {
  // Use instance.domain or instance.subdomain if domain is not available
  const displayDomain = instance.domain || (instance.subdomain ? `${instance.subdomain}.referme.site` : '');
  
  return (
    <TableRow key={instance.id}>
      <TableCell className="font-medium">{instance.name}</TableCell>
      <TableCell>{displayDomain}</TableCell>
      <TableCell>
        <InstancePlanBadge plan={instance.plan} />
      </TableCell>
      <TableCell>
        <InstanceStatusIndicator status={instance.status} />
      </TableCell>
      <TableCell>{new Date(instance.createdAt).toLocaleDateString()}</TableCell>
      <TableCell className="text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            align="end" 
            className="bg-white border border-gray-200 shadow-md"
          >
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onManage(instance.id)}>
              Manage
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onManagePayments(instance.id)}>
              <CreditCard className="mr-2 h-4 w-4" />
              Payment Settings
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onVisit(displayDomain)}>
              <ExternalLink className="mr-2 h-4 w-4" />
              Visit Site
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={() => onToggleSuspend(instance.id, instance.status)}
              className={instance.status === 'suspended' ? "text-green-600" : "text-amber-600"}
            >
              {instance.status === 'suspended' ? 'Reactivate' : 'Suspend'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
};

export default InstanceTableRow;
